// Layout — v1.1.0
//
// App shell for authenticated routes. Persistent left sidebar + top navbar.
// Public routes render outside this shell (see App.js).

import React from 'react';
import { NavLink } from 'react-router-dom';
import Navbar from './Navbar';
import PaywallBanner from './PaywallBanner';
import DevSubscriptionPanel from './DevSubscriptionPanel';

const SIDEBAR_WIDTH = 220;

const SECTIONS = [
  {
    label: 'Overview',
    links: [
      // BL-039 — Investor Intelligence Dashboard (Phase 1). The post-login
      // landing surface; this gives a persistent way back to it.
      { to: '/dashboard', label: 'Dashboard', icon: 'home' },
    ],
  },
  {
    label: 'Analyze',
    links: [
      // BL-017 — Opportunity Qualification triage, shown only when the feature
      // flag is enabled at build time (same convention as the mock routes).
      ...(process.env.REACT_APP_QUALIFICATION_ENABLED === 'true'
        ? [{ to: '/qualify', label: 'Qualify', icon: 'search' }]
        : []),
      { to: '/deal-analyzer', label: 'Deal Analyzer', icon: 'chart-bar' },
      { to: '/my-analyses',   label: 'My Analyses',   icon: 'folder' },
    ],
  },
  {
    label: 'Pipeline',
    links: [
      { to: '/leads', label: 'Leads', icon: 'users' },
    ],
  },
  {
    label: 'Market',
    links: [
      { to: '/discovery', label: 'Discovery', icon: 'search', disabled: true, soon: true },
    ],
  },
  {
    label: 'Account',
    links: [
      { to: '/account', label: 'Account', icon: 'user' },
      { to: '/pricing', label: 'Pricing', icon: 'star' },
      // BL-037 Story 37.2 — static self-service Help Center (entry point).
      { to: '/help', label: 'Help Center', icon: 'help' },
      // BL-037 Story 37.1 — in-app Feedback & Support capture.
      { to: '/support', label: 'Help & Feedback', icon: 'help' },
    ],
  },
];

const ICON_PATHS = {
  'home':      <path d="M3 11l9-8 9 8M5 10v10h14V10" />,
  'chart-bar': <path d="M3 3v18h18M7 14v4M12 10v8M17 6v12" />,
  'folder':    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />,
  'users':     <><circle cx="9" cy="8" r="4" /><path d="M3 21a6 6 0 0 1 12 0M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM22 21a4 4 0 0 0-4-4" /></>,
  'user':      <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a8 8 0 0 1 16 0v1" /></>,
  'star':      <path d="M12 2l2.4 7h7.6l-6 4.7 2.3 7.3L12 17l-6.3 4 2.3-7.3L2 9h7.6z" />,
  'search':    <><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>,
  'help':      <><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7M12 17h.01" /></>,
};

function Icon({ name }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

function SidebarLink({ to, label, icon, disabled, soon }) {
  const baseStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-3) var(--space-5)',
    borderLeft: '3px solid transparent',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-md)',
    fontWeight: 'var(--weight-medium)',
    textDecoration: 'none',
  };

  if (disabled) {
    return (
      <div
        title={soon ? 'Coming soon' : undefined}
        aria-disabled="true"
        style={{
          ...baseStyle,
          color: 'var(--color-muted)',
          cursor: 'not-allowed',
        }}
      >
        <Icon name={icon} />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        ...baseStyle,
        borderLeft: `3px solid ${isActive ? 'var(--color-amber)' : 'transparent'}`,
        background: isActive ? 'var(--color-navy-mid)' : 'transparent',
        color: isActive ? 'var(--color-white)' : 'var(--color-white-dim)',
        fontWeight: isActive ? 'var(--weight-semibold)' : 'var(--weight-medium)',
      })}
    >
      <Icon name={icon} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--color-navy)' }}>
      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside
        style={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          background: 'var(--color-navy)',
          color: 'var(--color-white)',
          borderRight: '1px solid var(--color-navy-border)',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
        }}
      >
        <nav style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-5) 0' }}>
          {SECTIONS.map((section) => (
            <div key={section.label} style={{ marginBottom: 'var(--space-5)' }}>
              <div
                style={{
                  padding: '0 var(--space-5)',
                  marginBottom: 'var(--space-2)',
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--weight-bold)',
                  letterSpacing: 'var(--tracking-label)',
                  textTransform: 'uppercase',
                  color: 'var(--color-steel)',
                }}
              >
                {section.label}
              </div>
              {section.links.map((link) => (
                <SidebarLink key={link.label} {...link} />
              ))}
            </div>
          ))}
        </nav>
        {/* Dev-only — gated internally by REACT_APP_STRIPE_MOCK env var */}
        <DevSubscriptionPanel />
      </aside>

      {/* ── Main column ─────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Navbar />
        <PaywallBanner />
        <main style={{ flex: 1, minHeight: 0 }}>
          {children}
        </main>
      </div>
    </div>
  );
}
